import {
  AccountCloseModal,
  AccountCreateModal,
  AccountList,
  AccountUpdateModal,
  AccountWorkspace,
  IbanRequestFlow,
  isIbanIssuingEligible
} from '@account'
import accountsStyles from '@account/scss/accounts.module.scss'
import {
  BankOutlineIcon,
  CustomPreloader,
  MMButton,
  PageActions,
  PageHeader,
  PageShell,
  PageTitle,
  PaymentLinkIcon,
  PlusIcon
} from '@components'
import { usePreselectedAccount } from '@hooks'
import cn from '@integrations/classnames'
import toast from '@integrations/notifications'
import { useContext, useEffect, useMemo, useState } from '@integrations/react'
import { useNavigate } from '@integrations/routing'
import { useAppDispatch } from '@store'
import CreateTicketModal from '@support/components/CreateTicketModal/CreateTicketModal'
import { isOpenAccount } from '@utils'
import {
  api,
  isOwnerOrAdministrator,
  paths,
  selectCurrentUserRole,
  useAppSelector,
  useGetSupportedSchemesAndCurrenciesQuery,
  useMerchantAccounts,
  useMerchantQry,
  useMyAccountAccessQry
} from 'mmfintech-backend-api'
import type { AccountBalanceResponse } from 'mmfintech-backend-api/types'
import { GlobalContext, isValidObject, tr } from 'mmfintech-commons'
import { ErrorDisplay } from 'mmfintech-portal-commons'

const EMPTY_IBAN_CURRENCIES = Object.freeze({})

function AccountsView() {
  const { modalShow } = useContext(GlobalContext)

  const customerRole = useAppSelector(selectCurrentUserRole)
  const { merchant } = useMerchantQry()

  const { paymentAccounts, accountsError, accountsLoading } = useMerchantAccounts()
  const { hasAnyWritePermission, hasWritePermission, getAccountAccessLevel } = useMyAccountAccessQry()

  const { ibanCurrencies } = useGetSupportedSchemesAndCurrenciesQuery(undefined, {
    selectFromResult: ({ data }) => ({
      ibanCurrencies: data?.currencies ?? EMPTY_IBAN_CURRENCIES
    })
  })

  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const accounts = useMemo(() => (paymentAccounts ?? []).filter(isOpenAccount), [paymentAccounts])
  const [selectedId, setSelectedId] = useState<number | undefined>()
  const [ibanFlow, setIbanFlow] = useState<{ accountId?: number } | null>(null)
  const [ticketOpen, setTicketOpen] = useState(false)

  const selectedAccount = accounts.find((account) => account.id === selectedId) ?? accounts[0]

  useEffect(() => {
    if (accounts.length && !accounts.some((account) => account.id === selectedId)) {
      setSelectedId(accounts[0].id)
    }
  }, [accounts, selectedId])

  // Opens on the account named in the router state (the staff Overview cards), once the accounts have loaded.
  usePreselectedAccount(accounts, (account) => setSelectedId(account.id))

  // `ibanCurrencies` is keyed by scheme ({ SEPA: ['EUR'], SWIFT: ['USD'] }), not by currency code
  const ibanSupportedCurrencies = Object.values(ibanCurrencies).flat()
  const selectedIbanSupported = ibanSupportedCurrencies.includes(selectedAccount?.currencyCode)
  const canIssueIban = isIbanIssuingEligible(customerRole, merchant?.capabilities) && isValidObject(ibanCurrencies)
  const isAdmin = isOwnerOrAdministrator(customerRole)
  const hasWriteAccessToSelectedAccount = hasWritePermission(selectedAccount?.id)

  function reloadAccounts() {
    dispatch(api.util.invalidateTags(['Accounts']))
  }

  function handleNewAccountClick() {
    modalShow({
      options: { closeOnClickOutside: false, size: 'auto', overflow: 'none' },
      content: (
        <AccountCreateModal
          onSuccess={(currencyCode: string, accountId: number) => {
            setSelectedId(accountId)
            toast.remove()
            toast.success(
              tr('FRONTEND.ACCOUNTS.SUCCESS_MESSAGE', '{{CURRENCY}} account successfully added', {
                CURRENCY: currencyCode
              })
            )
          }}
        />
      )
    })
  }

  function handleRenameClick(account: AccountBalanceResponse) {
    modalShow({
      options: { closeOnClickOutside: false, size: 'auto', overflow: 'none' },
      content: <AccountUpdateModal account={account} onSuccess={reloadAccounts} />
    })
  }

  function handleCloseAccountClick(account: AccountBalanceResponse) {
    // Select the account below the closed one (or above it, when it was last in the list)
    const index = accounts.findIndex(({ id }) => id === account.id)
    const nextAccount = accounts[index + 1] ?? accounts[index - 1]

    modalShow({
      options: { closeOnClickOutside: false, size: 'auto', overflow: 'none' },
      content: (
        <AccountCloseModal
          account={account}
          onSuccess={() => {
            setSelectedId(nextAccount?.id)
            toast.remove()
            toast.success(
              tr('FRONTEND.ACCOUNTS.CLOSE.SUCCESS_NAMED', '{{CURRENCY}} account “{{NAME}}” successfully closed', {
                CURRENCY: account.currencyCode,
                NAME: account.name
              })
            )
          }}
          onContactSupport={() => setTicketOpen(true)}
        />
      )
    })
  }

  function handleTicketSuccess(ticketId: number) {
    setTicketOpen(false)
    navigate(paths.support.ticketDetail(ticketId))
  }

  return (
    <PageShell className={cn('accounts-page-wrapper')}>
      <PageHeader>
        <PageTitle>{tr('FRONTEND.ACCOUNTS.TITLE', 'Accounts')}</PageTitle>
        <PageActions>
          {Boolean(merchant?.capabilities?.enablePortalWalletCheckout) && hasAnyWritePermission && (
            <MMButton
              variant='dashboard'
              leftIcon={<PaymentLinkIcon />}
              caption={tr('FRONTEND.DASHBOARD.BUTTON_PAYMENT_LINK', 'Payment link')}
              onClick={() => navigate(paths.scanAndPay())}
              data-test='button-payment-link'
            />
          )}
          {canIssueIban && (
            <MMButton
              variant='dashboard'
              leftIcon={<BankOutlineIcon size={14} />}
              caption={tr('FRONTEND.ACCOUNTS.REQUEST_IBAN_BUTTON', 'Request IBAN')}
              onClick={() => setIbanFlow({})}
              data-test='button-iban-request'
            />
          )}
          {isAdmin && (
            <MMButton
              variant='primary'
              leftIcon={<PlusIcon />}
              caption={tr('FRONTEND.ACCOUNTS.NEW_ACCOUNT_BUTTON', 'New account')}
              onClick={handleNewAccountClick}
              data-test='button-new-account'
            />
          )}
        </PageActions>
      </PageHeader>

      {accountsLoading ? (
        <CustomPreloader />
      ) : accountsError ? (
        <ErrorDisplay error={accountsError} />
      ) : (
        <div className={cn(accountsStyles['accounts-layout'], 'accounts-layout')}>
          <AccountList
            accounts={accounts}
            selectedId={selectedAccount?.id}
            onSelect={(a) => setSelectedId(a.id)}
            getAccessLevel={isAdmin ? undefined : getAccountAccessLevel}
          />

          {selectedAccount && (
            <AccountWorkspace
              account={selectedAccount}
              onViewTransactions={() => navigate(paths.banking.transactions.listByAccountId(selectedAccount.id))}
              onRename={isAdmin ? () => handleRenameClick(selectedAccount) : null}
              onCloseAccount={isAdmin ? () => handleCloseAccountClick(selectedAccount) : null}
              onDeposit={
                hasWriteAccessToSelectedAccount
                  ? () => navigate(paths.banking.deposit(), { state: { preSelectedAccount: selectedAccount.id } })
                  : null
              }
              onWithdraw={
                hasWriteAccessToSelectedAccount
                  ? () => navigate(paths.withdraw.select(), { state: { preSelectedAccount: selectedAccount.id } })
                  : null
              }
              onExchange={
                hasWriteAccessToSelectedAccount
                  ? () => navigate(paths.banking.exchange(), { state: { preSelectedAccount: selectedAccount.id } })
                  : null
              }
              onPayout={
                hasWriteAccessToSelectedAccount
                  ? () => navigate(paths.payout.select(), { state: { preSelectedAccount: selectedAccount.id } })
                  : null
              }
              ibanSupported={selectedIbanSupported}
              onRequestIban={
                canIssueIban && selectedIbanSupported ? () => setIbanFlow({ accountId: selectedAccount.id }) : null
              }
            />
          )}
        </div>
      )}

      {ibanFlow && (
        <IbanRequestFlow
          selectedAccountId={ibanFlow.accountId}
          onClose={() => setIbanFlow(null)}
          reload={reloadAccounts}
        />
      )}

      {ticketOpen && <CreateTicketModal onSuccess={handleTicketSuccess} onClose={() => setTicketOpen(false)} />}
    </PageShell>
  )
}

export default AccountsView
